import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth, JUST_AUTHENTICATED_FLAG, api } from '../context/AuthContext';

function validatePasswordStrength(password) {
  if (password.length < 8) return 'Use at least 8 characters.';
  if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) return 'Use a mix of letters and digits.';
  return null;
}

export default function ResetPassword() {
  const { toast } = useToast();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    const strengthErr = validatePasswordStrength(password);
    if (strengthErr) { setError(strengthErr); return; }
    if (password !== confirmPw) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/auth/password/reset', { token, password });
      sessionStorage.setItem(JUST_AUTHENTICATED_FLAG, '1');
      setSession(null, data.user);
      toast({ title: 'Password updated', description: 'You are now signed in.' });
      navigate('/');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not reset password. Try requesting a new link.';
      setError(msg);
    } finally {
      setBusy(false);
    }
    // Module-level stable imports (`API`, `axios`, `JUST_AUTHENTICATED_FLAG`) intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, password, confirmPw, setSession, toast, navigate]);

  if (!token) {
    return (
      <div className="bg-brand-cream min-h-screen">
        <section className="bg-brand-green text-brand-cream py-16">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
            <h1 className="font-serif text-4xl md:text-5xl leading-tight">Reset link missing</h1>
            <p className="mt-4 text-brand-cream/75">
              This page needs a token from your reset email. Request a new link below.
            </p>
          </div>
        </section>
        <section className="py-14 text-center">
          <Link to="/forgot-password" className="inline-block px-6 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold">
            Request a new link
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-brand-cream min-h-screen">
      <section className="bg-brand-green text-brand-cream py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Account recovery</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">Choose a new password</h1>
          <p className="mt-4 text-brand-cream/75">Pick something 8+ characters with a mix of letters and digits.</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-md mx-auto px-5 sm:px-8">
          <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-cream-dark p-8 md:p-10 space-y-5">
            <div>
              <label htmlFor="rp-password" className="block text-sm text-brand-green/70 mb-2">New password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                <input
                  id="rp-password"
                  data-testid="reset-password-input"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-green/60 hover:text-brand-green"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="rp-confirm" className="block text-sm text-brand-green/70 mb-2">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                <input
                  id="rp-confirm"
                  data-testid="reset-password-confirm"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                  placeholder="Re-enter your new password"
                />
              </div>
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.01] transition-transform disabled:opacity-70"
            >
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>) : (<>Update password <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
